<template>
  <data-table-wrapper
    :filter-config="filterConfig"
    :default-sort="{ by: 'billableDuration.start.timestamp', desc: true }"
    :default-items-per-page="15"
    :enable-search="true"
    :search-config="{
      urlKey: 'search',
      placeholder: 'Suchen...',
      debounce: 100,
    }"
    :footer-props="{ 'items-per-page-options': [15, 25, 50] }"
    @filters-changed="onFiltersChanged"
  >
    <template
      #default="{
        tableProps,
        tableEvents,
        filters,
        updateFilter,
        searchValue,
        updateSearch,
      }"
    >
      <div class="time-sheets-list-wrapper">
        <!-- Fixed Header -->
        <v-card flat class="time-sheets-list-header">
          <!-- Row 1: Search and Sort -->
          <v-toolbar flat dense class="rounded-0">
            <v-text-field
              :value="searchValue"
              @input="updateSearch"
              dense
              outlined
              hide-details="auto"
              placeholder="Suchen"
              prepend-inner-icon="mdi-magnify"
              clearable
              style="max-width: 200px"
              class="mr-2"
            ></v-text-field>

            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn small depressed v-bind="attrs" v-on="on">
                  <v-icon small left>mdi-sort</v-icon>
                  {{ getSortLabel(tableProps.sortBy, tableProps.sortDesc) }}
                  <v-icon small right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense nav>
                <v-list-item
                  v-for="option in sortOptions"
                  :key="option.value + '-' + option.desc"
                  @click="updateSort(option.value, option.desc, tableEvents)"
                  :class="{
                    'v-list-item--active': tableProps.sortBy[0] === option.value && tableProps.sortDesc[0] === option.desc,
                  }"
                >
                  <v-list-item-icon class="mr-3">
                    <v-icon small>{{ option.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ option.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="tableProps.sortBy[0] === option.value && tableProps.sortDesc[0] === option.desc">
                    <v-icon small color="primary">mdi-check</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn
              small
              depressed
              class="mx-1"
              @click="expandAllGroups"
              :disabled="!hasClosedGroups"
            >
              <v-icon small>mdi-unfold-more-horizontal</v-icon>
            </v-btn>
            <v-btn
              small
              depressed
              @click="collapseAllGroups"
              :disabled="!hasOpenGroups"
            >
              <v-icon small>mdi-unfold-less-horizontal</v-icon>
            </v-btn>
          </v-toolbar>
          <v-divider></v-divider>

          <!-- Row 2: Filters -->
          <v-toolbar flat dense class="rounded-0">
            <!-- Status Filter Menu -->
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn small depressed v-bind="attrs" v-on="on" class="mr-1">
                  <v-icon small left>mdi-filter-variant</v-icon>
                  {{ getStatusFilterLabel(filters.status) }}
                  <v-icon small right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense nav>
                <v-list-item
                  v-for="option in statusOptions"
                  :key="option.value"
                  @click="updateFilter('status', option.value)"
                  :class="{
                    'v-list-item--active': filters.status === option.value,
                  }"
                >
                  <v-list-item-icon v-if="option.icon" class="mr-3">
                    <v-icon small>{{ option.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ option.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="filters.status === option.value">
                    <v-icon small color="primary">mdi-check</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Assigned To Filter Menu -->
            <v-menu v-if="showAllTimeSheets" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn small depressed v-bind="attrs" v-on="on" class="mr-1">
                  <v-icon small left>mdi-account</v-icon>
                  {{ getPersonFilterLabel(filters.personFilter) }}
                  <v-icon small right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense nav>
                <v-list-item
                  v-for="option in personFilterOptions"
                  :key="option.value"
                  @click="updateFilter('personFilter', option.value)"
                  :class="{
                    'v-list-item--active': filters.personFilter === option.value,
                  }"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ option.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action
                    v-if="filters.personFilter === option.value"
                  >
                    <v-icon small color="primary">mdi-check</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Source Filter Menu -->
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn small depressed v-bind="attrs" v-on="on" class="mr-1">
                  <v-icon small left>mdi-source-branch</v-icon>
                  {{ getSourceFilterLabel(filters.sourceFilter) }}
                  <v-icon small right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense nav>
                <v-list-item
                  v-for="option in sourceFilterOptions"
                  :key="option.value"
                  @click="updateFilter('sourceFilter', option.value)"
                  :class="{
                    'v-list-item--active': filters.sourceFilter === option.value,
                  }"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ option.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action
                    v-if="filters.sourceFilter === option.value"
                  >
                    <v-icon small color="primary">mdi-check</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-toolbar>
          <v-divider></v-divider>
        </v-card>

        <!-- Data Table -->
        <v-card flat class="time-sheets-list-content">
          <!-- Loading State -->
          <template v-if="loading">
            <v-list>
              <v-list-item v-for="i in 5" :key="i">
                <v-list-item-content>
                  <v-skeleton-loader
                    type="list-item-two-line"
                  ></v-skeleton-loader>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>

          <!-- Loaded Content -->
          <v-data-table
            v-else
            v-bind="tableProps"
            v-on="tableEvents"
            :headers="headers"
            :items="filteredTimeSheets"
            :search="searchValue"
            :custom-filter="customFilter"
            group-by="monthGroup"
            hide-default-header
            :footer-props="footerProps"
            dense
            class="compact-table"
          >
            <!-- Group Header -->
            <template v-slot:group.header="{ group, headers, toggle, isOpen }">
              <td :colspan="headers.length" class="pa-0 rounded-0">
                <th :colspan="headers.length">
                  <v-btn
                    @click="() => { toggle(); updateGroupState(); }"
                    small
                    icon
                    class="mr-1"
                    :ref="group"
                    :data-open="isOpen"
                  >
                    <v-icon>
                      {{ isOpen ? "mdi-chevron-down" : "mdi-chevron-right" }}
                    </v-icon>
                  </v-btn>
                  {{ formatGroupLabel(group) }}
                  <span class="font-weight-regular grey--text">
                    ({{ getCountOfTimeSheetsInMonth(group) }})
                  </span>
                </th>
              </td>
            </template>

            <!-- Item Row -->
            <template v-slot:item="{ item }">
              <tr
                :class="{ 'selected-item': item.meta.id === selectedId }"
                @click="selectTimeSheet(item)"
                style="cursor: pointer"
              >
                <td class="pa-0">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        <div>{{ item.title || "Ohne Titel" }}</div>
                      </v-list-item-title>
                      <v-list-item-subtitle class="mt-1">
                        <time-sheet-status-chip
                          :status="item.status"
                          small
                          class="mr-1"
                        />
                        <time-sheet-type-chip
                          v-if="item.type"
                          :type="item.type"
                          small
                          class="mr-1"
                        />
                      </v-list-item-subtitle>
                      <v-list-item-subtitle class="mt-1">
                        <div style="line-height: 1.6">
                          <div>
                            <v-icon small left>mdi-clock-outline</v-icon>
                            <span
                              :class="
                                !item.billableDuration?.start?.timestamp
                                  ? 'grey--text'
                                  : ''
                              "
                            >
                              {{ formatTimeRange(item.billableDuration) }}
                            </span>
                          </div>
                          <div>
                            <v-icon small left>mdi-account</v-icon>
                            {{ item.user?.displayName || "Nicht zugewiesen" }}
                          </div>
                          <div v-if="item.payrollType?.title">
                            <v-icon small left>mdi-cash</v-icon>
                            {{ item.payrollType.title }}
                          </div>
                        </div>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </td>
              </tr>
            </template>

            <!-- No Data -->
            <template v-slot:no-data>
              <div class="pa-8 text-center">
                <v-icon size="64" color="grey lighten-1"
                  >mdi-clock-outline</v-icon
                >
                <div class="mt-4 text-body-1 grey--text">
                  Keine Stundenmeldungen gefunden
                </div>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </template>
  </data-table-wrapper>
</template>

<script>
import { auth } from "@/firebase";
import TimeSheetStatusChip from "@/components/accounting/time-sheets/TimeSheetStatusChip.vue";
import TimeSheetTypeChip from "@/components/accounting/time-sheets/TimeSheetTypeChip.vue";
import DataTableWrapper from "@/components/_system/DataTableWrapper.vue";
import { ACCOUNTING_TIME_SHEETS_read_all } from "@/data/permission-types.js";

export default {
  name: "TimeSheetsList",
  components: {
    TimeSheetStatusChip,
    TimeSheetTypeChip,
    DataTableWrapper,
  },
  props: {
    timeSheets: {
      type: Array,
      required: true,
    },
    selectedId: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentUserUid: auth.currentUser.uid,
      currentFilters: {},
      allGroupsExpanded: true,
      hasClosedGroups: false,
      hasOpenGroups: true,
      statusOptions: [
        { value: "all", text: "Alle Status", icon: "mdi-format-list-bulleted" },
        { value: "toBeReviewed", text: "Zu prüfen", icon: "mdi-check-all" },
        { value: "approved", text: "Genehmigt", icon: "mdi-check-circle" },
        { value: "rejected", text: "Abgelehnt", icon: "mdi-close-circle" },
        { value: "completed", text: "Abgeschlossen", icon: "mdi-flag-checkered" },
      ],
      personFilterOptions: [
        { value: "all", text: "Alle Personen" },
        { value: "own", text: "Eigene Meldungen" },
      ],
      sourceFilterOptions: [
        { value: "all", text: "Alle Quellen" },
        { value: "manual", text: "Manuell" },
        { value: "dutyReport", text: "Dienstbericht" },
      ],
      sortOptions: [
        { value: "billableDuration.start.timestamp", desc: true, text: "Neueste zuerst", icon: "mdi-sort-calendar-descending" },
        { value: "billableDuration.start.timestamp", desc: false, text: "Älteste zuerst", icon: "mdi-sort-calendar-ascending" },
      ],
      footerProps: {
        "items-per-page-options": [25, 50, 100, 150, 200, -1],
      },
    };
  },
  computed: {
    showAllTimeSheets() {
      return this.$store.getters["organization/checkPermissionByID"](
        `${ACCOUNTING_TIME_SHEETS_read_all}`
      );
    },

    /**
     * Filter configuration for DataTableWrapper
     */
    filterConfig() {
      return {
        status: { default: "all", urlKey: "status" },
        personFilter: { default: "own", urlKey: "personFilter" },
        sourceFilter: { default: "all", urlKey: "sourceFilter" },
      };
    },

    /**
     * Headers for v-data-table (hidden but needed)
     */
    headers() {
      return [
        {
          text: "Stundenmeldung",
          value: "title",
          sortable: false,
        },
      ];
    },

    /**
     * Filtered time sheets based on current filters
     */
    filteredTimeSheets() {
      let filtered = [...this.timeSheets];

      // Apply status filter
      if (this.currentFilters.status && this.currentFilters.status !== "all") {
        filtered = filtered.filter(
          (timeSheet) => timeSheet.status === this.currentFilters.status
        );
      }

      // Apply person filter
      if (
        this.currentFilters.personFilter &&
        this.currentFilters.personFilter !== "all"
      ) {
        if (this.currentFilters.personFilter === "own") {
          filtered = filtered.filter(
            (timeSheet) => timeSheet.user?.uid === this.currentUserUid
          );
        }
      }

      // Apply source filter
      if (
        this.currentFilters.sourceFilter &&
        this.currentFilters.sourceFilter !== "all"
      ) {
        if (this.currentFilters.sourceFilter === "dutyReport") {
          filtered = filtered.filter(
            (timeSheet) => timeSheet.type === "dutyReport"
          );
        } else if (this.currentFilters.sourceFilter === "manual") {
          filtered = filtered.filter(
            (timeSheet) => timeSheet.type !== "dutyReport"
          );
        }
      }

      // Always add month group for grouping
      filtered = filtered.map((timeSheet) => {
        const date = timeSheet.billableDuration?.start?.timestamp?.toDate?.();
        let monthGroup = "Kein Datum";

        if (date) {
          const month = ("0" + (date.getMonth() + 1)).slice(-2);
          const year = date.getFullYear();
          const monthLong = date.toLocaleString("de-DE", { month: "long" });
          // Format: "2026-01 - Januar 2026" for proper sorting
          monthGroup = `${year}-${month} - ${monthLong} ${year}`;
        }

        return {
          ...timeSheet,
          monthGroup,
        };
      });

      return filtered;
    },
  },
  methods: {
    onFiltersChanged(filters) {
      this.currentFilters = filters;
    },

    /**
     * Custom filter function for search
     */
    customFilter(value, search, item) {
      if (!search) return true;
      const searchLower = search.toLowerCase();

      return (
        item.title?.toLowerCase().includes(searchLower) ||
        item.user?.displayName?.toLowerCase().includes(searchLower) ||
        item.payrollType?.title?.toLowerCase().includes(searchLower) ||
        item.status?.toLowerCase().includes(searchLower)
      );
    },

    /**
     * Get label for status filter
     */
    getStatusFilterLabel(status) {
      const option = this.statusOptions.find((opt) => opt.value === status);
      return option ? option.text : "Status";
    },

    /**
     * Get label for person filter
     */
    getPersonFilterLabel(personFilter) {
      const option = this.personFilterOptions.find(
        (opt) => opt.value === personFilter
      );
      return option ? option.text : "Personen";
    },

    /**
     * Get label for source filter
     */
    getSourceFilterLabel(sourceFilter) {
      const option = this.sourceFilterOptions.find(
        (opt) => opt.value === sourceFilter
      );
      return option ? option.text : "Quelle";
    },

    selectTimeSheet(timeSheet) {
      this.$emit("select-time-sheet", timeSheet);
    },

    formatGroupLabel(group) {
      // Strip the "YYYY-MM - " prefix from the group label
      if (!group) return group;
      const parts = group.split(" - ");
      return parts.length > 1 ? parts[1] : group;
    },

    formatTimeRange(duration) {
      if (!duration?.start?.timestamp) {
        return "Nicht festgelegt";
      }

      const startDate = duration.start.timestamp.toDate();
      const startFormatted = startDate.toLocaleString("de-DE", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      if (duration.end?.timestamp) {
        const endDate = duration.end.timestamp.toDate();
        
        const sameDay = 
          startDate.getFullYear() === endDate.getFullYear() &&
          startDate.getMonth() === endDate.getMonth() &&
          startDate.getDate() === endDate.getDate();

        if (sameDay) {
          const endTime = endDate.toLocaleString("de-DE", {
            hour: "2-digit",
            minute: "2-digit",
          });
          return `${startFormatted} - ${endTime}`;
        } else {
          const endFormatted = endDate.toLocaleString("de-DE", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          return `${startFormatted} - ${endFormatted}`;
        }
      }

      return startFormatted;
    },

    getSortLabel(sortBy, sortDesc) {
      if (!sortBy || sortBy.length === 0) return "Sortieren";
      const option = this.sortOptions.find(
        (opt) => opt.value === sortBy[0] && opt.desc === sortDesc[0]
      );
      return option ? option.text : "Sortieren";
    },

    updateSort(value, desc, tableEvents) {
      tableEvents["update:sort-by"]([value]);
      tableEvents["update:sort-desc"]([desc]);
    },

    getCountOfTimeSheetsInMonth(monthGroup) {
      return this.filteredTimeSheets.filter((timeSheet) => timeSheet.monthGroup === monthGroup)
        .length;
    },

    expandAllGroups() {
      Object.keys(this.$refs).forEach((key) => {
        const ref = this.$refs[key];
        if (ref && ref.$attrs && !ref.$attrs["data-open"]) {
          ref.$el.click();
        }
      });
      this.allGroupsExpanded = true;
      this.hasClosedGroups = false;
      this.hasOpenGroups = true;
    },

    collapseAllGroups() {
      Object.keys(this.$refs).forEach((key) => {
        const ref = this.$refs[key];
        if (ref && ref.$attrs && ref.$attrs["data-open"]) {
          ref.$el.click();
        }
      });
      this.allGroupsExpanded = false;
      this.hasClosedGroups = true;
      this.hasOpenGroups = false;
    },

    onGroupToggle() {
      this.$nextTick(() => {
        const refs = Object.keys(this.$refs);
        if (refs.length === 0) {
          this.hasOpenGroups = false;
          this.hasClosedGroups = false;
          this.allGroupsExpanded = true;
          return;
        }

        const openGroups = [];
        const closedGroups = [];

        refs.forEach((key) => {
          const ref = this.$refs[key];
          if (ref && ref.$attrs) {
            if (ref.$attrs["data-open"]) {
              openGroups.push(key);
            } else {
              closedGroups.push(key);
            }
          }
        });

        this.hasOpenGroups = openGroups.length > 0;
        this.hasClosedGroups = closedGroups.length > 0;
        this.allGroupsExpanded =
          closedGroups.length === 0 && openGroups.length > 0;
      });
    },

    updateGroupState() {
      this.onGroupToggle();
    },

    resetGroupState() {
      this.$nextTick(() => {
        this.onGroupToggle();
      });
    },
  },
  watch: {
    filteredTimeSheets() {
      this.$nextTick(() => {
        this.resetGroupState();
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.resetGroupState();
    });
  },
  updated() {
    this.$nextTick(() => {
      this.resetGroupState();
    });
  },
};
</script>

<style scoped>
.time-sheets-list-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.time-sheets-list-header {
  flex-shrink: 0;
}

.time-sheets-list-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.compact-table >>> tbody tr {
  cursor: pointer;
}

.compact-table >>> .v-data-table__wrapper {
  overflow: visible;
}

.group-header {
  cursor: pointer;
  user-select: none;
}

.selected-item {
  background-color: rgba(25, 118, 210, 0.12) !important;
  border-left: 3px solid var(--v-primary-base, #1976d2);
}

.selected-item:hover {
  background-color: rgba(25, 118, 210, 0.16) !important;
}

/* Dark mode support */
.theme--dark .selected-item {
  background-color: rgba(25, 118, 210, 0.24) !important;
}

.theme--dark .selected-item:hover {
  background-color: rgba(25, 118, 210, 0.32) !important;
}
</style>
